import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Listbox } from "@headlessui/react";
import toast from "react-hot-toast";

import Select from "@/components/Select";
import api from "@/services/api";
import { PAPPERS_KEY } from "@/services/config";
import useStore from "@/services/store";
import { NAF_SECTIONS, TYPES, getEntrepriseActivityFromNaf, getEntrepriseTypeFromEffectif as getEntrepriseCategoryFromEffectif } from "@/utils";
import { useNavigate } from "react-router-dom";

const Form = () => {
  return (
    <div className="max-w-3xl mx-auto">
      <CompanySelect />
      <CompanyForm />
    </div>
  );
};

const CompanySelect = () => {
  const [searchParams] = useSearchParams();
  const { type } = useParams();
  const plan = searchParams.get("plan");
  const [myCompanies, setMyCompanies] = useState([]);
  const [hubspotCompany, setHubspotCompany] = useState(null);
  const [selected, setSelected] = useState(null);
  const [websiteLink, setWebsiteLink] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useStore();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const { ok, data } = await api.post("/company/search", { user_id: user._id });
      if (!ok) throw new Error("Error fetching data");
      setMyCompanies(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchHubspotCompany = async () => {
    if (!selected) return;
    const { ok, data } = await api.get(`/company/${selected._id}/hubspot`);
    if (!ok) throw new Error("Error fetching data");
    setHubspotCompany(data);
  };

  const handleUpdateWebsiteLink = async () => {
    if (!selected) return;
    const { ok, data } = await api.put(`/company/${selected._id}`, { website_link: websiteLink });
    if (!ok || !data.website_link) {
      console.log("Erreur lors de la mise à jour du site web de l'entreprise: ", data);
      toast.error("Erreur lors de la mise à jour du site web de l'entreprise.");
      return;
    }
    setSelected(data);
    toast.success("Site web de l'entreprise enregistré avec succès.");
  };

  const handleStart = async () => {
    try {
      setLoading(true);
      let paymentURL = "";
      if (type.toUpperCase() === "START-UP" && plan === "ACCELERATED") paymentURL = "/payment/accelerated-start-up";
      else if (type.toUpperCase() === "START-UP" && plan === "CERTIFIED") paymentURL = "/payment/certified-start-up";
      else if (type.toUpperCase() === "SME" && plan === "ACCELERATED") paymentURL = "/payment/accelerated-sme";
      else paymentURL = "/payment/certified-sme";

      const { ok, data } = await api.post(paymentURL, { companyId: selected._id, hubspotCompanyId: hubspotCompany?.id ?? null });

      if (!ok || !data.url) {
        console.log("error: ", data);
        toast.error("Erreur lors du traitement du paiement. Veuillez réessayer.");
        return;
      }
      window.location.href = data.url;
    } catch (error) {
      console.error(error);
      toast.error("Erreur inattendue lors de la mise à jour de l'entreprise. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchHubspotCompany();
  }, [selected]);

  if (!myCompanies.length) return null;

  return (
    <>
      <div className="flex flex-col justify-center items-center">
        <button
          className="mb-4 text-base font-medium text-primary flex items-center justify-center hover:text-primary/80 transition-colors duration-200 ease-in-out"
          onClick={() => navigate("/valuation/new")}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Revenir au choix de notation
        </button>
        <h2 className="text-xl font-semibold text-black text-center">Démarrez une notation avec une start-up existante</h2>
        <div className="flex items-center justify-center text-base font-medium text-primary m-4">
          <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            Vous avez choisi une notation {type} {getPlanLabel(plan)}
          </span>
        </div>
        <Select
          className="py-4"
          items={myCompanies}
          selected={selected}
          onSelect={(v) => setSelected(v)}
          getOptionValue={(item) => item._id}
          getOptionLabel={(item) => item.company_name}
          renderOption={CompanyOption}
        />
      </div>
      {selected && !selected.website_link && (
        <div className="w-full my-2 flex justify-center items-center gap-4">
          <div className="w-full flex flex-col mt-2">
            <span className="text-sm text-gray-500">
              Veuillez renseigner le site web de l'entreprise avant de commencer la notation<span className="text-red-500">*</span>
            </span>
            <div className="flex flex-row gap-2 mt-2">
              <input
                type="url"
                placeholder="https://www.example.com"
                value={websiteLink || ""}
                onChange={(e) => setWebsiteLink(e.target.value)}
                className="flex-grow p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button onClick={handleUpdateWebsiteLink} disabled={!websiteLink} className="primary-button whitespace-nowrap min-w-[120px]">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && selected.website_link && (
        <div className="w-full my-2 flex justify-center items-center gap-4">
          <button className="primary-button" onClick={handleStart} disabled={loading}>
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                Chargement...
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
            ) : (
              "Continuer"
            )}
          </button>
        </div>
      )}
      <div className="my-4 flex flex-col justify-center items-center gap-2">
        <p className="text-sm text-gray-600 text-center">
          Vous pouvez retrouver toutes vos notations en cours d'évaluation dans la section{" "}
          <Link to="/my-service" className="text-primary font-medium hover:underline">
            Mes services
          </Link>
          .
        </p>
      </div>
      <div className="flex items-center flex-row my-4">
        <div className="border-t border-primary w-2/5" />
        <div className="w-1/5 flex items-center justify-center">
          <div className=" w-16 h-16 border border-primary  text-primary rounded-full flex items-center justify-center">OU</div>
        </div>
        <div className="border-t border-primary w-2/5" />
      </div>
    </>
  );
};

const CompanyForm = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { type } = useParams();
  const plan = searchParams.get("plan") || "ACCELERATED";
  const [values, setValues] = useState({
    company_name: "",
    company_website: "",
    company_siren: "",
    company_category: "",
    company_activity: "",
  });
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const navigate = useNavigate();
  const companiesRef = useRef(null);

  // close the dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companiesRef.current && !companiesRef.current.contains(event.target)) {
        setCompanies([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!search) return setCompanies([]);

    const timeout = setTimeout(() => {
      if (search.length < 1) return;
      fetchPappersByName();
    }, 500);

    return () => clearTimeout(timeout);
  }, [search]);

  const fetchPappersByName = async () => {
    setSearching(true);

    try {
      const url = `https://api.pappers.fr/v2/recherche?q=${encodeURIComponent(search)}&api_token=${PAPPERS_KEY}`;
      const response = await fetch(url, { "content-type": "application/json" });
      const data = await response.json();
      setCompanies(data.resultats || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setSearching(false);
    }
  };

  const fetchPappersBySiren = async (siren) => {
    const apiUrl = `https://api.pappers.fr/v2/entreprise?siren=${siren}&api_token=${PAPPERS_KEY}`;
    const headers = { "content-type": "application/json" };
    try {
      const response = await fetch(apiUrl, { headers });
      const data = await response.json();
      setValues({
        ...values,
        metadata: data,
        company_name: data.nom_entreprise,
        company_category: getEntrepriseCategoryFromEffectif(data.effectif_min),
        company_activity: getEntrepriseActivityFromNaf(data.code_naf),
        company_siren: data.siren,
        company_started_at: new Date(data.date_creation),
        city: data.siege?.ville || "",
        postal_code: data.siege?.code_postal || "",
        country: data.siege?.pays || "",
        turnover_2022: data.finances.filter((data) => data.annee === 2022)[0]?.chiffre_affaires,
        turnover_2023: data.finances.filter((data) => data.annee === 2023)[0]?.chiffre_affaires,
        turnover_2024: data.finances.filter((data) => data.annee === 2024)[0]?.chiffre_affaires,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleOptionClick = async (data) => {
    await fetchPappersBySiren(data.siren);
    toast.success("Certaines données de l'entreprise ont été pré-remplies. Veuillez vérifier les informations.");
    setCompanies([]);
    setSearch("");
  };

  const handleCreate = async () => {
    try {
      setLoading(true);
      const resCompany = await api.post("/company", values);
      if (!resCompany.ok) {
        toast.error("Erreur lors de la création de l'entreprise. Veuillez réessayer.");
        return;
      }
      let paymentURL = "";
      if (type.toUpperCase() === "START-UP" && plan === "ACCELERATED") paymentURL = "/payment/accelerated-start-up";
      else if (type.toUpperCase() === "START-UP" && plan === "CERTIFIED") paymentURL = "/payment/certified-start-up";
      else if (type.toUpperCase() === "SME" && plan === "ACCELERATED") paymentURL = "/payment/accelerated-sme";
      else paymentURL = "/payment/certified-sme";

      const resPayment = await api.post(paymentURL, { companyId: resCompany.data._id, hubspotCompanyId: resCompany.companyHubspot.id });

      if (!resPayment.ok || !resPayment.data.url) {
        console.log("error: ", resPayment.data);
        toast.error("Erreur lors du traitement du paiement. Veuillez réessayer.");
        return;
      }
      window.location.href = resPayment.data.url;
    } catch (error) {
      console.error(error);
      toast.error("Erreur inattendue lors de la création de l'entreprise. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="h-full flex flex-col items-center gap-6">
      <h2 className="text-xl font-semibold text-center text-black">Démarrez une évaluation avec une nouvelle entreprise</h2>
      <div className="flex items-center justify-center text-base font-medium text-primary">
        <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>
          Vous avez choisi une notation {type} {getPlanLabel(plan)}
        </span>
      </div>
      <div className="space-y-2 w-full">
        <p className="text-center text-black font-semibold">Nom de l'entreprise</p>
        <div className="relative w-full">
          <div className="relative mt-1">
            <input
              type="text"
              name="company_name"
              className="w-full input"
              placeholder="ACME"
              required
              value={values.company_name}
              onChange={(e) => setValues({ ...values, company_name: e.target.value })}
              onInput={(e) => setSearch(e.target.value)}
            />
          </div>
          {searching && (
            <div className="absolute mt-1 w-96 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none text-sm z-50">
              <div className="text-xs text-gray-500">Recherche en cours...</div>
            </div>
          )}
          {companies.length > 0 && (
            <div className="absolute mt-1 w-96 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none text-sm z-50" ref={companiesRef}>
              {companies.map((option, index) => (
                <div key={index} onClick={() => handleOptionClick(option)} className="cursor-pointer py-2 px-4 hover:bg-gray-100">
                  <span className={`block truncate`}>{option.nom_entreprise}</span>
                  <span className={`block truncate text-xs text-gray-500`}>{`SIREN: ${option.siren}`}</span>
                  <span className={`block truncate text-xs text-gray-500`}>{`${option.siege?.ville}, ${option.siege?.code_postal}, ${option.siege?.adresse || ""}`}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2 w-full">
        <p className="text-center text-black font-semibold">
          Site web <span className="text-red-500">*</span>
        </p>
        <input
          type="text"
          name="company_website"
          placeholder="https://www.acme.com"
          value={values.website_link}
          onChange={(e) => setValues({ ...values, website_link: e.target.value })}
          className="input"
          required
        />
      </div>
      <div className="space-y-2 w-full">
        <p className="text-center text-black font-semibold">Siren</p>
        <input
          type="text"
          name="company_siren"
          placeholder="Saisissez le numero SIREN"
          value={values.company_siren}
          onChange={(e) => setValues({ ...values, company_siren: e.target.value })}
          className="input"
        />
      </div>

      <div className="space-y-2 w-full">
        <p className="text-center text-black font-semibold">Quelle est la taille votre entreprise ?</p>
        <select className="select" value={values.company_category} onChange={(e) => setValues((prev) => ({ ...prev, company_category: e.target.value }))}>
          <option value="" disabled>
            Choisir la taille d'entreprise
          </option>
          {TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-2">
        <p className="text-center text-black font-semibold">Dans quel secteur d'activité votre entreprise excerce-t-elle ?</p>
        <select className="select" value={values.company_activity} onChange={(e) => setValues((prev) => ({ ...prev, company_activity: e.target.value }))}>
          <option value="" disabled>
            Choisir le secteur d'activité
          </option>
          {Object.entries(NAF_SECTIONS).map(([key, value]) => (
            <option key={key} value={value}>
              {`${key} - ${value}`}
            </option>
          ))}
        </select>
      </div>
      <div className="w-full flex justify-center items-center gap-4">
        <div className="flex justify-between w-1/2 my-10">
          <Link to="/valuation/new" className="empty-button disabled:hover:bg-white px-10">
            {t("previous")}
          </Link>
          {values.company_name && values.company_category && values.company_activity && values.website_link && (
            <button
              onClick={handleCreate}
              className="primary-button px-10 transition-all duration-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 min-w-fit"
              disabled={loading}>
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  Création
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </span>
              ) : (
                "Continuer"
              )}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const CompanyOption = ({ item }) => {
  return (
    <Listbox.Option value={item} className="cursor-pointer select-none py-2 pl-3 pr-9">
      {({ selected }) => (
        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>{item.company_name}</span>
            {item.valuation && <span className="bg-primary p-1 rounded  text-xs text-white">Évaluation en cours</span>}
          </div>
          {item.website_link && (
            <span className="text-xs text-gray-500 flex items-center gap-[2px]">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                />
              </svg>
              {item.website_link}
            </span>
          )}
        </div>
      )}
    </Listbox.Option>
  );
};

const getPlanLabel = (plan) => {
  if (plan === "ACCELERATED") return "accélérée";
  if (plan === "CERTIFIED") return "certifiée";
};

export default Form;
